import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Lock, Percent, Save, Timer, Unlock } from 'lucide-react';
import { enumLabel, ApiClientError, EmptyState, Spinner, StatusBadge } from '@riskpay/ui-components';
import { api } from '../lib/client';

/* ==========================================================================
 *  Merchant management
 *
 *  Three controls, each with real consequences, each requiring a written reason
 *  that lands in the immutable audit log:
 *
 *    · MDR override      — per method, in basis points, so a rate is always an
 *                          integer and 2.95% can never become 2.9500000000004.
 *    · Settlement cycle  — T+0 through T+7, plus the rolling reserve.
 *    · Freeze            — the kill switch. Blocks all new processing at once.
 * ========================================================================== */

const METHODS = ['UPI', 'UPI_INTENT', 'CREDIT_CARD', 'DEBIT_CARD', 'NET_BANKING', 'WALLET', 'EMI'] as const;

interface MerchantRow {
  id: string;
  merchant_ref: string;
  legal_name: string;
  display_name: string;
  kyc_status: string;
  risk_tier: string;
  settlement_cycle: string;
  rolling_reserve_bps: number;
  default_mdr_bps: number;
  mdr_config: Record<string, { bps: number; flat_minor: number }> | string;
  is_frozen: number;
  frozen_reason: string | null;
}

function parseMdr(config: MerchantRow['mdr_config']): Record<string, { bps: number; flat_minor: number }> {
  if (typeof config === 'string') {
    try {
      return JSON.parse(config);
    } catch {
      return {};
    }
  }
  return config ?? {};
}

function MerchantControls({ merchant, onDone }: { merchant: MerchantRow; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [mdr, setMdr] = useState(() => {
    const parsed = parseMdr(merchant.mdr_config);
    return Object.fromEntries(
      METHODS.map((m) => [m, { bps: parsed[m]?.bps ?? merchant.default_mdr_bps, flat_minor: parsed[m]?.flat_minor ?? 0 }]),
    );
  });
  const [cycle, setCycle] = useState(merchant.settlement_cycle);
  const [reserveBps, setReserveBps] = useState(merchant.rolling_reserve_bps);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['merchants'] });
  const fail = (err: unknown) => setError(err instanceof ApiClientError ? err.message : 'The change was rejected.');

  const saveMdr = useMutation({
    mutationFn: () => api.post(`/merchants/${merchant.id}/mdr`, { mdr_config: mdr, reason }),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: fail,
  });

  const saveCycle = useMutation({
    mutationFn: () =>
      api.post(`/merchants/${merchant.id}/settlement-cycle`, {
        settlement_cycle: cycle,
        rolling_reserve_bps: reserveBps,
        reason,
      }),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: fail,
  });

  const toggleFreeze = useMutation({
    mutationFn: () => api.post(`/merchants/${merchant.id}/freeze`, { frozen: merchant.is_frozen !== 1, reason }),
    onSuccess: () => {
      setError(null);
      void invalidate();
      onDone();
    },
    onError: fail,
  });

  const reasonTooShort = reason.trim().length < 5;

  return (
    <div className="space-y-4 border-t border-line px-4 py-4">
      {error && (
        <p role="alert" className="rounded border border-danger-ring bg-danger-soft px-3 py-2.5 text-xs text-[#FDA4AF]">
          {error}
        </p>
      )}

      {/* Reason is first, not last: it applies to every action below, and
          putting it at the end invites the habit of typing it as an afterthought. */}
      <div>
        <label htmlFor={`reason-${merchant.id}`} className="mb-1.5 block text-2xs uppercase tracking-wider text-content-subtle">
          Reason for this change · required, written to the audit log
        </label>
        <input
          id={`reason-${merchant.id}`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Negotiated rate for Q3 volume commitment, approved by Sales"
          className="input text-xs"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* ---- MDR override ------------------------------------------- */}
        <section className="panel-inset p-3">
          <h4 className="flex items-center gap-2 text-xs font-medium">
            <Percent className="h-3.5 w-3.5 text-content-subtle" aria-hidden="true" />
            MDR by method
          </h4>
          <div className="mt-3 space-y-2">
            {METHODS.map((method) => (
              <div key={method} className="flex items-center gap-2">
                <span className="w-28 shrink-0 text-2xs text-content-muted">{enumLabel(method)}</span>
                <div className="relative flex-1">
                  <input
                    type="number"
                    min={0}
                    max={30}
                    step={0.01}
                    value={(mdr[method]!.bps / 100).toFixed(2)}
                    onChange={(e) =>
                      setMdr((current) => ({
                        ...current,
                        [method]: { ...current[method]!, bps: Math.round(Number(e.target.value) * 100) },
                      }))
                    }
                    className="input numeral h-7 pr-6 text-2xs"
                    aria-label={`${method} percentage`}
                  />
                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-2xs text-content-faint">
                    %
                  </span>
                </div>
                <div className="relative w-24">
                  <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-2xs text-content-faint">
                    ₹
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={(mdr[method]!.flat_minor / 100).toFixed(2)}
                    onChange={(e) =>
                      setMdr((current) => ({
                        ...current,
                        [method]: { ...current[method]!, flat_minor: Math.round(Number(e.target.value) * 100) },
                      }))
                    }
                    className="input numeral h-7 pl-5 text-2xs"
                    aria-label={`${method} flat fee`}
                  />
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => saveMdr.mutate()}
            disabled={reasonTooShort || saveMdr.isPending}
            className="btn btn-primary mt-3 h-8 w-full text-xs"
          >
            {saveMdr.isPending ? <Spinner className="h-3.5 w-3.5" /> : <Save className="h-3.5 w-3.5" />}
            Save MDR override
          </button>
        </section>

        {/* ---- Cycle + reserve + freeze -------------------------------- */}
        <div className="space-y-4">
          <section className="panel-inset p-3">
            <h4 className="flex items-center gap-2 text-xs font-medium">
              <Timer className="h-3.5 w-3.5 text-content-subtle" aria-hidden="true" />
              Settlement terms
            </h4>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <div>
                <label htmlFor={`cycle-${merchant.id}`} className="mb-1.5 block text-2xs text-content-subtle">
                  Cycle
                </label>
                <select
                  id={`cycle-${merchant.id}`}
                  value={cycle}
                  onChange={(e) => setCycle(e.target.value)}
                  className="input h-7 text-2xs"
                >
                  {['T0', 'T1', 'T2', 'T3', 'T7', 'MANUAL'].map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor={`reserve-${merchant.id}`} className="mb-1.5 block text-2xs text-content-subtle">
                  Rolling reserve
                </label>
                <div className="relative">
                  <input
                    id={`reserve-${merchant.id}`}
                    type="number"
                    min={0}
                    max={50}
                    step={0.5}
                    value={(reserveBps / 100).toFixed(2)}
                    onChange={(e) => setReserveBps(Math.round(Number(e.target.value) * 100))}
                    className="input numeral h-7 pr-6 text-2xs"
                  />
                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-2xs text-content-faint">
                    %
                  </span>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => saveCycle.mutate()}
              disabled={reasonTooShort || saveCycle.isPending}
              className="btn btn-secondary mt-3 h-8 w-full text-xs"
            >
              {saveCycle.isPending ? <Spinner className="h-3.5 w-3.5" /> : <Save className="h-3.5 w-3.5" />}
              Save settlement terms
            </button>
          </section>

          <section className={`panel-inset p-3 ${merchant.is_frozen ? 'border-danger-ring' : ''}`}>
            <h4 className="flex items-center gap-2 text-xs font-medium">
              {merchant.is_frozen ? (
                <Unlock className="h-3.5 w-3.5 text-brand-400" aria-hidden="true" />
              ) : (
                <Lock className="h-3.5 w-3.5 text-danger" aria-hidden="true" />
              )}
              {merchant.is_frozen ? 'Unfreeze account' : 'Freeze account'}
            </h4>
            <p className="mt-2 text-2xs leading-relaxed text-content-subtle">
              {merchant.is_frozen
                ? `Currently frozen: ${merchant.frozen_reason ?? 'no reason recorded'}. Unfreezing restores processing immediately.`
                : 'Blocks every new payment, refund and payout at once. The dashboard stays readable so the merchant can see history and reach support.'}
            </p>
            <button
              type="button"
              onClick={() => toggleFreeze.mutate()}
              disabled={reasonTooShort || toggleFreeze.isPending}
              className={`btn mt-3 h-8 w-full text-xs ${merchant.is_frozen ? 'btn-primary' : 'btn-danger'}`}
            >
              {toggleFreeze.isPending ? <Spinner className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
              {merchant.is_frozen ? 'Unfreeze merchant' : 'Freeze merchant now'}
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}

export function Merchants() {
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['merchants'],
    queryFn: () => api.get<{ data: MerchantRow[] }>('/merchants'),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-20 text-sm text-content-muted">
        <Spinner /> Loading merchants
      </div>
    );
  }

  const merchants = data?.data ?? [];

  return (
    <div className="space-y-3">
      <p className="text-xs text-content-muted">
        Commercial terms and the account kill switch. Every change below requires a written reason and is recorded
        against your account permanently.
      </p>

      {merchants.length === 0 ? (
        <div className="panel">
          <EmptyState
            title="No merchants to manage"
            description="Approved merchants appear here once compliance has cleared them."
          />
        </div>
      ) : (
        <ul className="space-y-2">
          {merchants.map((merchant) => (
            <li key={merchant.id} className="panel overflow-hidden">
              <button
                type="button"
                onClick={() => setExpanded(expanded === merchant.id ? null : merchant.id)}
                className="flex w-full flex-wrap items-center justify-between gap-4 px-4 py-4 text-left transition-colors hover:bg-surface-overlay"
                aria-expanded={expanded === merchant.id}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium">{merchant.legal_name}</p>
                  <p className="numeral mt-1 text-2xs text-content-faint">
                    {merchant.merchant_ref} · {merchant.settlement_cycle} ·{' '}
                    {(merchant.default_mdr_bps / 100).toFixed(2)}% default MDR
                    {merchant.rolling_reserve_bps > 0 && ` · ${(merchant.rolling_reserve_bps / 100).toFixed(2)}% reserve`}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={merchant.kyc_status} />
                  <span
                    className={`badge ${merchant.risk_tier === 'LOW' ? 'badge-success' : merchant.risk_tier === 'MEDIUM' ? 'badge-warning' : 'badge-danger'}`}
                  >
                    {merchant.risk_tier}
                  </span>
                  {merchant.is_frozen === 1 && (
                    <span className="badge badge-danger">
                      <Lock className="h-3 w-3" />
                      frozen
                    </span>
                  )}
                </div>
              </button>

              {expanded === merchant.id && (
                <MerchantControls merchant={merchant} onDone={() => setExpanded(null)} />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
